import { Injectable, NgZone, OnDestroy } from '@angular/core';
import { BehaviorSubject, Observable, Subject, Subscription, interval } from 'rxjs';
import { tap } from 'rxjs/operators';
import { io, Socket } from 'socket.io-client';
import { environment } from '../../../environments/environment';
import { ApiService } from './api.service';
import { AuthService } from './auth.service';
import { logger } from '../utils/logger.util';
import {
  NotificationCompteurResponse,
  NotificationInterne,
  NotificationListeResponse,
  NotificationLueResponse,
  NotificationPagination,
  NotificationPurgeResponse,
  NotificationSocketNewPayload,
  NotificationSocketReadyPayload,
  NotificationSocketUnreadCountPayload,
  NotificationStatutFiltre,
  NotificationSuppressionResponse,
  NotificationToutesLuesResponse
} from '../models/notification-interne.model';

/**
 * Etat partagé de la cloche in-app : liste paginée, compteur non lues et flux
 * temps réel du namespace Socket.IO `/notifications`.
 *
 * Hybride, comme `RemoteActionStaffSyncService` : le socket porte le temps réel,
 * un poll du compteur toutes les 60 s sert de filet quand il n'est pas établi
 * (`ENABLE_NOTIFICATION_SOCKET=false`, proxy sans websocket, etc.).
 *
 * Le compteur est toujours celui renvoyé par le serveur — jamais dérivé de la
 * liste chargée, qui est paginée.
 */
@Injectable({ providedIn: 'root' })
export class NotificationsInternesService implements OnDestroy {
  private static readonly NAMESPACE = '/notifications';
  private static readonly PAGE_SIZE = 20;
  private static readonly COMPTEUR_POLL_MS = 60_000;

  private readonly endpoint = '/notifications';

  private readonly notificationsSubject = new BehaviorSubject<NotificationInterne[]>([]);
  private readonly nonLuesSubject = new BehaviorSubject<number>(0);
  private readonly chargementSubject = new BehaviorSubject<boolean>(false);
  private readonly socketReadySubject = new BehaviorSubject<boolean>(false);
  private readonly nouvelleNotificationSubject = new Subject<NotificationInterne>();
  private readonly ouverturePanneauSubject = new BehaviorSubject<NotificationStatutFiltre | null>(
    null
  );

  readonly notifications$ = this.notificationsSubject.asObservable();
  readonly nonLues$ = this.nonLuesSubject.asObservable();
  readonly chargement$ = this.chargementSubject.asObservable();
  readonly socketReady$ = this.socketReadySubject.asObservable();
  readonly nouvelleNotification$ = this.nouvelleNotificationSubject.asObservable();

  /**
   * Demande d'ouverture du panneau de la cloche, émise par le toast global.
   * `BehaviorSubject` volontairement : la cloche du dashboard est créée *après*
   * la navigation et doit quand même recevoir la demande.
   */
  readonly ouverturePanneauDemandee$ = this.ouverturePanneauSubject.asObservable();

  private socket: Socket | null = null;
  private pollSubscription: Subscription | null = null;
  private authSubscription: Subscription | null = null;
  private statut: NotificationStatutFiltre = 'toutes';
  private pagination: NotificationPagination | null = null;
  private initialized = false;

  constructor(
    private readonly apiService: ApiService,
    private readonly authService: AuthService,
    private readonly ngZone: NgZone
  ) {
    this.authSubscription = this.authService.currentUser$.subscribe(user => {
      if (!user && this.initialized) {
        this.reset();
      }
    });
  }

  ngOnDestroy(): void {
    this.authSubscription?.unsubscribe();
    this.authSubscription = null;
    this.arreterPollCompteur();
    this.deconnecterSocket();
  }

  /** Idempotent : appelable depuis chaque dashboard portant la cloche. */
  init(): void {
    if (this.initialized || !this.authService.isLoggedIn()) {
      return;
    }
    this.initialized = true;
    this.rafraichirCompteur();
    this.connecterSocket();
    this.demarrerPollCompteur();
  }

  /** Purge l'état local et coupe le temps réel (déconnexion). */
  reset(): void {
    this.initialized = false;
    this.arreterPollCompteur();
    this.deconnecterSocket();
    this.pagination = null;
    this.statut = 'toutes';
    this.notificationsSubject.next([]);
    this.nonLuesSubject.next(0);
    this.ouverturePanneauSubject.next(null);
  }

  /** Demande l'ouverture du panneau de la cloche sur le dashboard courant. */
  demanderOuverturePanneau(statut: NotificationStatutFiltre = 'toutes'): void {
    this.ouverturePanneauSubject.next(statut);
  }

  /** Appelé par la cloche réellement affichée, une fois le panneau ouvert. */
  consommerOuverturePanneau(): void {
    if (this.ouverturePanneauSubject.value !== null) {
      this.ouverturePanneauSubject.next(null);
    }
  }

  getStatut(): NotificationStatutFiltre {
    return this.statut;
  }

  getNonLues(): number {
    return this.nonLuesSubject.value;
  }

  /** Vrai s'il reste au moins une page à charger pour le filtre courant. */
  peutChargerPlus(): boolean {
    return !!this.pagination && this.pagination.page < this.pagination.totalPages;
  }

  /** Charge la première page du filtre demandé et remplace la liste courante. */
  charger(statut: NotificationStatutFiltre = this.statut): Observable<NotificationListeResponse> {
    this.statut = statut;
    return this.chargerPage(1, false);
  }

  /** Ajoute la page suivante à la liste courante (défilement infini / bouton). */
  chargerPlus(): Observable<NotificationListeResponse> | null {
    if (!this.peutChargerPlus() || this.chargementSubject.value) {
      return null;
    }
    return this.chargerPage(this.pagination!.page + 1, true);
  }

  marquerLue(id: number): Observable<NotificationLueResponse> {
    return this.apiService.patch<NotificationLueResponse>(`${this.endpoint}/${id}/lue`, {}).pipe(
      tap(response => {
        this.remplacerNotification(response.data.notification);
        this.nonLuesSubject.next(response.data.nonLues);
      })
    );
  }

  marquerToutesLues(): Observable<NotificationToutesLuesResponse> {
    return this.apiService.patch<NotificationToutesLuesResponse>(`${this.endpoint}/lues`, {}).pipe(
      tap(response => {
        const lueAt = new Date().toISOString();
        const liste = this.notificationsSubject.value.map(notification =>
          notification.lueAt ? notification : { ...notification, lueAt }
        );
        this.notificationsSubject.next(this.statut === 'non-lues' ? [] : liste);
        this.nonLuesSubject.next(response.data.nonLues);
      })
    );
  }

  supprimer(id: number): Observable<NotificationSuppressionResponse> {
    return this.apiService.delete<NotificationSuppressionResponse>(`${this.endpoint}/${id}`).pipe(
      tap(response => {
        this.notificationsSubject.next(
          this.notificationsSubject.value.filter(notification => notification.id !== id)
        );
        this.decrementerTotal(1);
        this.nonLuesSubject.next(response.data.nonLues);
      })
    );
  }

  /** Purge côté serveur toutes les notifications déjà lues. */
  purgerLues(): Observable<NotificationPurgeResponse> {
    return this.apiService.delete<NotificationPurgeResponse>(`${this.endpoint}/lues`).pipe(
      tap(response => {
        const restantes = this.notificationsSubject.value.filter(notification => !notification.lueAt);
        this.notificationsSubject.next(restantes);
        this.decrementerTotal(response.data.supprimees);
      })
    );
  }

  rafraichirCompteur(): void {
    this.apiService
      .get<NotificationCompteurResponse>(`${this.endpoint}/non-lues/count`)
      .subscribe({
        next: response => this.nonLuesSubject.next(response.data.nonLues),
        error: error => logger.debug('[Notifications] compteur indisponible', { error })
      });
  }

  private chargerPage(page: number, ajouter: boolean): Observable<NotificationListeResponse> {
    this.chargementSubject.next(true);

    return this.apiService
      .get<NotificationListeResponse>(this.endpoint, {
        page,
        limit: NotificationsInternesService.PAGE_SIZE,
        statut: this.statut
      })
      .pipe(
        tap({
      next: response => {
            const recues = (response.data.notifications ?? []).filter(
              (n) => n.evenement !== 'MESSAGE_FAMILLE_RECU'
            );
            this.notificationsSubject.next(
              ajouter ? [...this.notificationsSubject.value, ...recues] : recues
            );
            this.pagination = response.data.pagination;
            this.nonLuesSubject.next(response.data.nonLues);
            this.chargementSubject.next(false);
          },
          error: () => this.chargementSubject.next(false)
        })
      );
  }

  private remplacerNotification(notification: NotificationInterne): void {
    const liste = this.notificationsSubject.value.map(item =>
      item.id === notification.id ? notification : item
    );
    this.notificationsSubject.next(
      this.statut === 'non-lues' ? liste.filter(item => !item.lueAt) : liste
    );
  }

  private decrementerTotal(nombre: number): void {
    if (!this.pagination) {
      return;
    }
    const total = Math.max(0, this.pagination.total - nombre);
    this.pagination = {
      ...this.pagination,
      total,
      totalPages: Math.max(1, Math.ceil(total / this.pagination.limit))
    };
  }

  private demarrerPollCompteur(): void {
    if (this.pollSubscription) {
      return;
    }
    this.pollSubscription = interval(NotificationsInternesService.COMPTEUR_POLL_MS).subscribe(() => {
      if (!this.socketReadySubject.value) {
        this.rafraichirCompteur();
      }
    });
  }

  private arreterPollCompteur(): void {
    this.pollSubscription?.unsubscribe();
    this.pollSubscription = null;
  }

  private connecterSocket(): void {
    const url = this.resolveSocketUrl();
    const token = this.authService.getAccessToken();
    if (!url || !token) {
      logger.debug('[Notifications] socket non connecte — URL ou token absent');
      return;
    }

    const socket = io(`${url}${NotificationsInternesService.NAMESPACE}`, {
      auth: { token },
      withCredentials: true,
      transports: ['websocket', 'polling'],
      reconnection: true,
      reconnectionDelayMax: 10_000
    });
    this.socket = socket;

    socket.on('notification:ready', (payload: NotificationSocketReadyPayload) => {
      this.ngZone.run(() => {
        this.socketReadySubject.next(true);
        this.nonLuesSubject.next(payload?.nonLues ?? 0);
      });
    });

    socket.on('notification:new', (payload: NotificationSocketNewPayload) => {
      this.ngZone.run(() => {
        const notification = payload?.notification;
        if (!notification?.id) {
          return;
        }
        // Filet front : actions à distance réservées tech / admin / superadmin
        if (this.estRemoteActionMasquee(notification)) {
          return;
        }
        // Messages individuels famille → badge icône Messages, pas la cloche
        if (notification.evenement === 'MESSAGE_FAMILLE_RECU') {
          this.nonLuesSubject.next(payload.nonLues ?? this.nonLuesSubject.value);
          this.nouvelleNotificationSubject.next(notification);
          return;
        }
        if (this.statut !== 'lues') {
          const deja = this.notificationsSubject.value.some(item => item.id === notification.id);
          if (!deja) {
            this.notificationsSubject.next([notification, ...this.notificationsSubject.value]);
          }
        }
        this.nonLuesSubject.next(payload.nonLues ?? this.nonLuesSubject.value + 1);
        this.nouvelleNotificationSubject.next(notification);
      });
    });

    socket.on('notification:unread-count', (payload: NotificationSocketUnreadCountPayload) => {
      this.ngZone.run(() => this.nonLuesSubject.next(payload?.nonLues ?? 0));
    });

    socket.on('disconnect', (reason: string) => {
      this.ngZone.run(() => {
        logger.debug('[Notifications] socket deconnecte', { reason });
        this.socketReadySubject.next(false);
      });
    });

    // Le handshake ne rafraîchit pas le JWT : on relit le token courant avant
    // chaque nouvelle tentative pour survivre à un refresh d'access token.
    socket.on('connect_error', (error: Error) => {
      this.ngZone.run(() => {
        this.socketReadySubject.next(false);
        socket.auth = { token: this.authService.getAccessToken() ?? '' };
        logger.debug('[Notifications] connect_error', { message: error?.message });
      });
    });
  }

  private deconnecterSocket(): void {
    if (!this.socket) {
      return;
    }
    try {
      this.socket.removeAllListeners();
      this.socket.disconnect();
    } finally {
      this.socket = null;
    }
    this.socketReadySubject.next(false);
  }

  /**
   * Strip le suffixe `/api` de `environment.apiUrl` : sinon socket.io-client
   * traite `/api` comme une partie du namespace.
   */
  private resolveSocketUrl(): string | null {
    const base = (environment.apiUrl ?? '').replace(/\/+$/, '');
    if (!base) {
      return null;
    }
    return base.endsWith('/api') ? base.slice(0, -4) : base;
  }

  /** Actions à distance : réservées tech / admin / superadmin (filet front). */
  private estRemoteActionMasquee(notification: NotificationInterne): boolean {
    const evenement = notification.evenement;
    if (evenement !== 'REMOTE_ACTION_FAILED' && evenement !== 'REMOTE_ACTION_EXPIRED') {
      return false;
    }
    const role = this.authService.getCurrentUser()?.role?.nom;
    return role !== 'technicien' && role !== 'admin' && role !== 'superadmin';
  }
}
